import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getUsersSuggestions,
  clearSuggestions,
} from "../../store/slices/userSlice";
import { UserSuggestion } from "../../types";
import { UserIcon } from "lucide-react";
import { User } from "../../types";
import { useTranslation } from "react-i18next";

/*  TYPES */
type SearchField = "driver" | "admin" | "user";

interface UserAutoCompleteProps {
  value: string;
  onChange: (value: string) => void;
  onUserSelect: (user: UserSuggestion) => void;
  searchField?: SearchField;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  users: User[];
  size?: "sm" | "md";
}

export const UserAutoComplete: React.FC<UserAutoCompleteProps> = ({
  value,
  onChange,
  onUserSelect,
  searchField = "driver",
  placeholder = "Enter Driver Name",
  disabled = false,
  error = false,
  className = "",
  size = "md",
}) => {
  const { t } = useTranslation("shipments");

  const dispatch = useAppDispatch();
  const { suggestions, loading } = useAppSelector((state) => state.users);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  /*  Debounced search */
  const debouncedSearch = useCallback(
    (term: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        if (term.trim().length >= 2) {
          let type = "driver";
          if (searchField != "driver") {
            type = "admin";
          }
          dispatch(
            getUsersSuggestions({
              query: term.trim(),
              limit: 10,
              type: type,
            }),
          );
          setIsOpen(true);
        } else {
          dispatch(clearSuggestions());
          setIsOpen(false);
        }
      }, 750);
    },
    [dispatch, searchField],
  );

  /*  Handlers */

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    setSearchTerm(newVal);
    onChange(newVal);
    debouncedSearch(newVal);
  };

  const handleUserSelect = (user: UserSuggestion) => {
    const label = `${user.firstName} ${user.lastName}`;
    setSearchTerm(label);
    onChange(label);
    onUserSelect(user);
    setIsOpen(false);
    dispatch(clearSuggestions());
  };

  /* Click‑outside to close */
  useEffect(() => {
    const listener = (evt: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(evt.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(evt.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, []);

  /* Sync external value */
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  /* Cleanup debounce on unmount */
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  /*  Styling helpers */
  const sizeCls =
    size === "sm"
      ? "h-8 px-2.5 py-1 text-[13px] rounded-md"
      : "px-3 py-2 rounded-lg";
  const base = `w-full ${sizeCls} border focus:ring-2 focus:border-transparent placeholder-gray-400`;
  const errorCls = error
    ? "border-red-300 focus:ring-red-500"
    : "border-gray-300 focus:ring-primary";
  const disCls = disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "";

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`${base} ${errorCls} ${disCls} ${className}`}
        autoComplete="off"
      />

      {loading && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="animate-spin h-4 w-4 rounded-full border-b-2 border-primary-600" />
        </div>
      )}

      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg"
        >
          {suggestions.length > 0
            ? suggestions.map((user) => {
                return (
                  <div
                    key={user._id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleUserSelect(user);
                    }}
                    className="cursor-pointer border-b border-gray-100 px-4 py-3 last:border-b-0 hover:bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 truncate">
                        <UserIcon className="h-4 w-4 text-gray-400 shrink-0" />
                        <span className="truncate font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </span>
                      </div>
                      {user.relevanceScore && (
                        <span className="shrink-0 text-xs text-gray-500">
                          {Math.round(parseFloat(user.relevanceScore) * 100)}%
                          match
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            : /* Empty‑state */
              !loading &&
              searchTerm.length >= 2 && (
                <div className="p-4 text-center text-gray-500">
                  {t("filters.noResults")} "{searchTerm}"
                </div>
              )}
        </div>
      )}
    </div>
  );
};
